import { Controller } from "@hotwired/stimulus"

// Combobox autocompletion for picking a friend to add to a group.
// Wraps a text input with a popover of matching friends. Selecting one sets
// the hidden friendship_id and submits the form. Pressing Enter without a
// selection submits the typed query (the controller resolves it server-side).
//
// Markup contract (form is the controller root):
//   <form data-controller="friend-combobox"
//         data-friend-combobox-friends-value="<json array of {id, name, email, initials, hue}>">
//     <input type="hidden" name="friendship_id" data-friend-combobox-target="hiddenId">
//     <div class="relative">
//       <input type="text" name="query"
//              data-friend-combobox-target="input"
//              data-action="input->friend-combobox#filter focus->friend-combobox#focus keydown->friend-combobox#keydown">
//       <div data-friend-combobox-target="menu" hidden>
//         <div data-friend-combobox-target="list"></div>
//       </div>
//     </div>
//   </form>
const MAX_RESULTS = 8

export default class extends Controller {
  static targets = ["input", "hiddenId", "menu", "list"]
  static values = { friends: Array }

  connect() {
    this._onDocumentClick = (event) => {
      if (!this.element.contains(event.target)) this.close()
    }
  }

  disconnect() {
    document.removeEventListener("click", this._onDocumentClick)
  }

  focus() {
    if (this.friendsValue.length === 0) return
    this._show(this._matches(this.inputTarget.value))
  }

  filter() {
    // User typed; any previously-picked friend no longer applies.
    this.hiddenIdTarget.value = ""
    const matches = this._matches(this.inputTarget.value)
    if (matches.length === 0) {
      this.close()
    } else {
      this._show(matches)
    }
  }

  keydown(event) {
    if (this.menuTarget.hidden) {
      if (event.key === "ArrowDown" && this.friendsValue.length > 0) {
        event.preventDefault()
        this.focus()
      }
      return
    }

    const options = this._optionElements()
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this._moveActive(1, options)
        break
      case "ArrowUp":
        event.preventDefault()
        this._moveActive(-1, options)
        break
      case "Enter": {
        const active = options.find((o) => o.dataset.active === "true")
        if (active) {
          event.preventDefault()
          this._pick(active)
        }
        // else: native form submit fires with the typed query.
        break
      }
      case "Escape":
        event.preventDefault()
        this.close()
        this.inputTarget.blur()
        break
      case "Tab":
        this.close()
        break
    }
  }

  select(event) {
    this._pick(event.currentTarget)
  }

  close() {
    this.menuTarget.hidden = true
    document.removeEventListener("click", this._onDocumentClick)
  }

  // ---- internals ----

  _show(matches) {
    this._render(matches)
    this.menuTarget.hidden = false
    document.addEventListener("click", this._onDocumentClick)
  }

  _matches(query) {
    const needle = (query || "").toLowerCase().trim()
    const list = this.friendsValue
    if (!needle) return list.slice(0, MAX_RESULTS)
    return list
      .filter(
        (f) =>
          f.name.toLowerCase().includes(needle) ||
          f.email.toLowerCase().includes(needle),
      )
      .slice(0, MAX_RESULTS)
  }

  _pick(optionEl) {
    const id = optionEl.dataset.id
    const name = optionEl.dataset.name
    this.hiddenIdTarget.value = id
    this.inputTarget.value = name
    this.close()
    this.element.requestSubmit()
  }

  _moveActive(direction, options) {
    if (options.length === 0) return
    const current = options.findIndex((o) => o.dataset.active === "true")
    const next = (current + direction + options.length) % options.length
    options.forEach((o, i) => {
      o.dataset.active = (i === next).toString()
    })
    options[next]?.scrollIntoView({ block: "nearest" })
  }

  _optionElements() {
    return Array.from(this.listTarget.querySelectorAll("[data-id]"))
  }

  _render(matches) {
    this.listTarget.innerHTML = matches
      .map(
        (f, i) => `
      <button type="button"
              data-action="click->friend-combobox#select"
              data-id="${this._escapeAttr(f.id)}"
              data-name="${this._escapeAttr(f.name)}"
              data-active="${i === 0 ? "true" : "false"}"
              class="flex items-center gap-3 w-full px-3 py-2 text-sm hover:bg-app-surface-active data-[active=true]:bg-app-surface-active text-left transition-colors">
        <span class="h-7 w-7 shrink-0 rounded-full inline-flex items-center justify-center text-[10px] font-display font-semibold text-white"
              style="background: linear-gradient(135deg, hsl(${f.hue}, 55%, 45%) 0%, hsl(${f.hue}, 65%, 28%) 100%);">
          ${this._escapeText(f.initials)}
        </span>
        <span class="min-w-0 flex-1">
          <span class="block text-app-text truncate">${this._escapeText(f.name)}</span>
          <span class="block text-app-text-faint text-xs truncate">${this._escapeText(f.email)}</span>
        </span>
      </button>
    `,
      )
      .join("")
  }

  _escapeText(s) {
    const div = document.createElement("div")
    div.textContent = String(s ?? "")
    return div.innerHTML
  }

  _escapeAttr(s) {
    return String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  }
}
